import React, {
  useCallback, useMemo, useRef, useState,
} from 'react';

import { createRoot } from 'react-dom/client';
import PropTypes from 'prop-types';
import { Button } from '@picsio/ui';

import localization from '../../../../shared/strings';
import Checkbox from '../../../../UIComponents/checkbox';
import Icon from '../../../Icon';
import ChannelTabsHeader from '../MediaSelection/ChannelTabsHeader';
import MediaThumbnailGrid from '../MediaSelection/MediaThumbnailGrid';
import ButtonLoader from '../ButtonLoader';
import styles from '../styles.module.scss';

function ProductMediaSelectionDialog({
  channelOptions,
  selectedChannels,
  assets,
  productMediaByChannel,
  validateBeforeFinish,
  onConfirm,
  onBack,
  onCancel,
  onValidationAbort,
  root,
}) {
  const strings = localization.DIALOGS.PRODUCT_DIALOGS.CONNECT_CHANNEL;
  const mediaStrings = strings.PRODUCT_MEDIA;

  const channels = useMemo(
    () => channelOptions.filter(({ value }) => selectedChannels.includes(value)),
    [channelOptions, selectedChannels],
  );

  const [activeChannelId, setActiveChannelId] = useState(channels[0]?.value);
  const [mediaByChannel, setMediaByChannel] = useState(() => (
    selectedChannels.reduce((acc, channelId) => ({
      ...acc,
      [channelId]: productMediaByChannel[channelId] || [],
    }), {})
  ));
  const [isConfirming, setIsConfirming] = useState(false);

  const activeChannel = channels.find(({ value }) => value === activeChannelId) || channels[0];
  const selectedAssetIds = mediaByChannel[activeChannel?.value] || [];
  const isMultiChannel = channels.length > 1;

  const allChecked = assets.length > 0 && selectedAssetIds.length === assets.length;
  const noneChecked = selectedAssetIds.length === 0;
  const selectAllValue = useMemo(() => {
    if (allChecked) return true;
    if (noneChecked) return false;
    return 'indeterminate';
  }, [allChecked, noneChecked]);

  const isDestroyedRef = useRef(false);

  const destroy = useCallback(() => {
    if (isDestroyedRef.current) {
      return;
    }

    isDestroyedRef.current = true;

    try {
      root.unmount();
    } catch { /* ignore */ }
  }, [root]);

  const handleCancel = useCallback(() => {
    destroy();
    onCancel?.();
  }, [destroy, onCancel]);

  const handlePrevious = useCallback(() => {
    destroy();
    onBack?.(mediaByChannel);
  }, [destroy, mediaByChannel, onBack]);

  const handleChannelChange = useCallback((channelId) => {
    setActiveChannelId(channelId);
  }, []);

  const handleSelectionChange = useCallback((nextSelectedAssetIds) => {
    setMediaByChannel((prev) => ({
      ...prev,
      [activeChannel.value]: nextSelectedAssetIds,
    }));
  }, [activeChannel.value]);

  const handleSelectAll = useCallback(() => {
    if (allChecked) {
      handleSelectionChange([]);
      return;
    }

    handleSelectionChange(assets.map(({ _id }) => _id));
  }, [allChecked, assets, handleSelectionChange]);

  const handleConfirm = useCallback(async () => {
    if (validateBeforeFinish) {
      setIsConfirming(true);

      try {
        await validateBeforeFinish(() => {
          setIsConfirming(false);
          destroy();
        });
        destroy();
        onConfirm(mediaByChannel);
      } catch {
        if (isDestroyedRef.current) {
          onValidationAbort?.();
          return;
        }

        setIsConfirming(false);
      }

      return;
    }

    destroy();
    onConfirm(mediaByChannel);
  }, [
    destroy,
    mediaByChannel,
    onConfirm,
    validateBeforeFinish,
    onValidationAbort,
  ]);

  const getBadgeCount = useCallback(
    (channelId) => (mediaByChannel[channelId] || []).length,
    [mediaByChannel],
  );

  const counterText = mediaStrings.COUNTER(
    selectedAssetIds.length,
    assets.length,
    activeChannel?.label || '',
  );

  const channelLabel = activeChannel?.label || '';

  const title = isMultiChannel
    ? mediaStrings.TITLE_MULTI
    : mediaStrings.TITLE_SINGLE(channelLabel);

  const description = isMultiChannel
    ? mediaStrings.DESCRIPTION_MULTI
    : mediaStrings.DESCRIPTION_SINGLE(channelLabel);

  // TODO (future): Handle unsupported media format for single-channel direct attach.

  return (
    <div className={`simpleDialog ${styles.connectChannelSectionModal} ${styles.connectChannelSectionModalWide} ${styles.connectChannelSectionDialogWithSubheader}`}>
      <div className="simpleDialogUnderlayer" />
      <div className={`simpleDialogBox ${styles.connectChannelSectionModalBoxWide}`}>
        <div className="simpleDialogHeader">
          <span className="simpleDialogTitle">{title}</span>
          <span
            role="button"
            tabIndex={0}
            className="simpleDialogBtnCross"
            onClick={handleCancel}
            onKeyDown={handleCancel}
          >
            <Icon name="close" />
          </span>
        </div>
        <div className="simpleDialogSubHeader">
          <p>{description}</p>
        </div>
        <div className="simpleDialogContent">
          <div className="simpleDialogContentInner">
            <If condition={isMultiChannel}>
              <ChannelTabsHeader
                channels={channels}
                activeChannelId={activeChannel.value}
                getBadgeCount={getBadgeCount}
                onChange={handleChannelChange}
              />
            </If>
            <div className={styles.connectChannelSectionMediaToolbar}>
              <Checkbox value={selectAllValue} onChange={handleSelectAll} />
              <span className={styles.connectChannelSectionMediaCounter}>{counterText}</span>
            </div>
            <div className={styles.connectChannelSectionMediaDivider} />
            <div className={styles.connectChannelSectionMediaGridWrapper}>
              <MediaThumbnailGrid
                assets={assets}
                selectedAssetIds={selectedAssetIds}
                onChange={handleSelectionChange}
              />
            </div>
          </div>
        </div>
        <div className={`simpleDialogFooter ${styles.connectChannelSectionMediaFooter}`}>
          <Button
            color="secondary"
            size="md"
            variant="contained"
            disabled={isConfirming}
            onClick={handleCancel}
          >
            {strings.BTN_CANCEL}
          </Button>
          <If condition={onBack}>
            <Button
              color="secondary"
              size="md"
              variant="contained"
              disabled={isConfirming}
              onClick={handlePrevious}
            >
              {strings.BTN_PREVIOUS}
            </Button>
          </If>
          <Button
            color="primary"
            size="md"
            variant="contained"
            disabled={isConfirming}
            endIcon={isConfirming ? <ButtonLoader /> : null}
            onClick={handleConfirm}
          >
            {strings.BTN_CONFIRM_MEDIA}
          </Button>
        </div>
      </div>
    </div>
  );
}

ProductMediaSelectionDialog.propTypes = {
  channelOptions: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
  })).isRequired,
  selectedChannels: PropTypes.arrayOf(PropTypes.string).isRequired,
  assets: PropTypes.arrayOf(PropTypes.shape({
    _id: PropTypes.string.isRequired,
  })).isRequired,
  productMediaByChannel: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string)),
  validateBeforeFinish: PropTypes.func,
  root: PropTypes.shape({ unmount: PropTypes.func.isRequired }).isRequired,
  onConfirm: PropTypes.func.isRequired,
  onBack: PropTypes.func,
  onCancel: PropTypes.func,
  onValidationAbort: PropTypes.func,
};

ProductMediaSelectionDialog.defaultProps = {
  productMediaByChannel: {},
  validateBeforeFinish: undefined,
  onBack: undefined,
  onCancel: undefined,
  onValidationAbort: undefined,
};

export function showProductMediaSelectionDialog(props) {
  const $wrapper = document.querySelector('.wrapperDialog');
  const root = createRoot($wrapper);
  root.render(
    <ProductMediaSelectionDialog
      {...props}
      root={root}
    />,
  );
}

export default ProductMediaSelectionDialog;
